import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Stack, LinearProgress, Alert, Button
} from '@mui/material';
import {
  TrendingUp,
  Warning,
  Build,
  Schedule,
  Insights
} from '@mui/icons-material';
import { useI18n } from '../../i18n/i18n';

const TechnicianMLAnalysis = ({ user }) => {
  const { t } = useI18n();
  const [predictions, setPredictions] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Імітація завантаження даних
    setTimeout(() => {
      const demoPredictions = [
        {
          machine: 'Lavadora L-12',
          issue: 'water_leak',
          priority: 'high',
          predictedTime: 2.5,
          confidence: 0.85
        },
        {
          machine: 'Secadora S-05', 
          issue: 'heating_element',
          priority: 'medium',
          predictedTime: 1.8,
          confidence: 0.72
        },
        {
          machine: 'Caldera Principal',
          issue: 'pressure_issue',
          priority: 'high',
          predictedTime: 3.2,
          confidence: 0.91
        },
        {
          machine: 'Introductor',
          issue: 'conveyor_belt',
          priority: 'medium',
          predictedTime: 1.5,
          confidence: 0.68
        }
      ];

      const demoRecommendations = [
        {
          type: 'preventive_maintenance',
          issue: 'water_leak',
          frequency: 3,
          message: t('recommendation_water_leak'),
          priority: 'high'
        },
        {
          type: 'inspection',
          issue: 'corrosion',
          frequency: 2,
          message: t('recommendation_corrosion'),
          priority: 'medium'
        }
      ];

      setPredictions(demoPredictions);
      setRecommendations(demoRecommendations);
      setLoading(false);
    }, 1000);
  }, [t]);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getPriorityText = (priority) => {
    switch (priority) {
      case 'high': return t('priority_high');
      case 'medium': return t('priority_medium');
      case 'low': return t('priority_low');
      default: return priority;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>{t('common_loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        {t('ml_analysis_title')}
      </Typography>
      <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
        {t('ml_analysis_subtitle')}
      </Typography>

      {/* Прогнози часу ремонту */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <TrendingUp sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('repair_time_predictions')}
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>{t('machine')}</strong></TableCell>
                  <TableCell><strong>{t('issue_type')}</strong></TableCell>
                  <TableCell><strong>{t('predicted_time')}</strong></TableCell>
                  <TableCell><strong>{t('confidence')}</strong></TableCell>
                  <TableCell><strong>{t('priority')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {predictions.map((prediction, index) => (
                  <TableRow key={index} hover>
                    <TableCell>{prediction.machine}</TableCell>
                    <TableCell>{t(prediction.issue) || prediction.issue}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {prediction.predictedTime.toFixed(1)} {t('hours')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={prediction.confidence * 100}
                          color={prediction.confidence > 0.8 ? 'success' : prediction.confidence > 0.6 ? 'warning' : 'error'}
                          sx={{ width: 60, height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="body2">
                          {(prediction.confidence * 100).toFixed(0)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getPriorityText(prediction.priority)}
                        color={getPriorityColor(prediction.priority)}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Рекомендації по профілактиці */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <Insights sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('maintenance_recommendations')}
          </Typography>
          
          <Stack spacing={2}>
            {recommendations.map((rec, index) => (
              <Alert 
                key={index}
                severity={rec.priority === 'high' ? 'warning' : 'info'}
                icon={rec.priority === 'high' ? <Warning /> : <Build />}
              >
                <Typography variant="body2" fontWeight="bold">
                  {rec.message}
                </Typography>
                <Typography variant="caption" display="block">
                  {t('frequency')}: {rec.frequency} {t('occurrences')}
                </Typography>
              </Alert>
            ))}
            
            {recommendations.length === 0 && (
              <Alert severity="success">
                {t('no_urgent_recommendations')}
              </Alert>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Статистика ефективності */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Schedule sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" color="primary.main" fontWeight="bold">
              2.3h
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('avg_repair_time')}
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <TrendingUp sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
            <Typography variant="h4" color="success.main" fontWeight="bold">
              87%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('prediction_accuracy')}
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Build sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
            <Typography variant="h4" color="info.main" fontWeight="bold">
              24
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('prevented_issues')}
            </Typography>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TechnicianMLAnalysis;